/* Kona 2026 — interaction layer
   1. Press feedback on pointer-down (never wait for click)
   2. Drawers (cart / search / menu) can be grabbed and thrown shut:
      1:1 tracking with grab offset, rubber-band past the open edge,
      momentum projection decides commit vs. cancel, velocity hands off to a spring,
      and a mid-flight grab interrupts from the presentation value.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- 1. Press feedback ------------------------------------------- */
  var PRESSABLE = '.c-btn, .btn, .product-card, .primary-nav__link, .header-trigger__link, .link--arrow, .collection-list__item';
  document.addEventListener('pointerdown', function (e) {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    var el = e.target.closest(PRESSABLE);
    if (!el) return;
    el.classList.add('is-pressed');
    var release = function () {
      el.classList.remove('is-pressed');
      window.removeEventListener('pointerup', release, true);
      window.removeEventListener('pointercancel', release, true);
    };
    window.addEventListener('pointerup', release, true);
    window.addEventListener('pointercancel', release, true);
  }, { passive: true });

  /* ---------- 2. Drawer physics ------------------------------------------- */
  // Apple's momentum projection (Designing Fluid Interfaces sample code)
  function project(velocityPxPerS, decelerationRate) {
    var d = decelerationRate || 0.998;
    return (velocityPxPerS / 1000) * d / (1 - d);
  }
  // Soft boundary: the further past the edge, the less the drawer follows
  function rubberband(overshoot, dimension, c) {
    c = c || 0.55;
    return (overshoot * dimension * c) / (dimension + c * Math.abs(overshoot));
  }
  function currentTranslateX(el) {
    var t = getComputedStyle(el).transform;
    if (!t || t === 'none') return 0;
    var m = t.match(/matrix(?:3d)?\(([^)]+)\)/);
    if (!m) return 0;
    var v = m[1].split(',').map(parseFloat);
    return v.length === 16 ? v[12] : v[4];
  }

  // Integrates a spring each frame so velocity can be handed in and re-targeted mid-flight.
  // damping ratio ζ and response R (seconds), per Apple's designer-facing parameters.
  function Spring(el, opts) {
    this.el = el;
    this.x = 0; this.v = 0; this.target = 0;
    this.zeta = opts.damping; this.omega = (2 * Math.PI) / opts.response;
    this.raf = null; this.last = 0; this.onSettle = null;
  }
  Spring.prototype.animateTo = function (target, x, v, onSettle) {
    this.target = target; this.x = x; this.v = v || 0; this.onSettle = onSettle || null;
    this.last = performance.now();
    if (!this.raf) this.raf = requestAnimationFrame(this.tick.bind(this));
    // rAF pauses in hidden tabs — never leave the drawer stranded mid-flight
    var self = this;
    clearTimeout(this.watchdog);
    this.watchdog = setTimeout(function () {
      if (!self.raf) return;
      cancelAnimationFrame(self.raf); self.raf = null;
      self.x = self.target; self.v = 0;
      self.el.style.transform = 'translate3d(' + self.x + 'px, 0, 0)';
      if (self.onSettle) self.onSettle();
    }, 1500);
  };
  Spring.prototype.stop = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
    clearTimeout(this.watchdog);
  };
  Spring.prototype.tick = function (now) {
    var dt = Math.min(0.032, (now - this.last) / 1000); this.last = now;
    // semi-implicit Euler on x'' = -ω²(x - target) - 2ζω x'
    var a = -this.omega * this.omega * (this.x - this.target) - 2 * this.zeta * this.omega * this.v;
    this.v += a * dt; this.x += this.v * dt;
    var done = Math.abs(this.x - this.target) < 0.3 && Math.abs(this.v) < 5;
    if (done) { this.x = this.target; this.v = 0; }
    this.el.style.transform = 'translate3d(' + this.x.toFixed(2) + 'px, 0, 0)';
    if (done) { this.raf = null; clearTimeout(this.watchdog); if (this.onSettle) this.onSettle(); }
    else this.raf = requestAnimationFrame(this.tick.bind(this));
  };

  function attachDrawer(wrap) {
    if (wrap.__kona) return;
    wrap.__kona = true;
    var width = wrap.getBoundingClientRect().width || 480;
    var spring = new Spring(wrap, { damping: 1.0, response: 0.35 });   // critically damped by default
    var ptr = null, startX = 0, startY = 0, grabX = 0, dragging = false, decided = false, hist = [];

    function bgEl() { return document.querySelector('.mfp-bg.mfp-draw'); }
    function setX(x) {
      wrap.style.transform = 'translate3d(' + x + 'px, 0, 0)';
      var bg = bgEl();
      if (bg) bg.style.opacity = String(Math.max(0, 1 - x / width));
    }
    function reset() {
      spring.stop();
      ptr = null; dragging = false;
      wrap.classList.remove('is-dragging', 'is-settling');
      wrap.style.transform = '';
      var bg = bgEl();
      if (bg) bg.style.opacity = '';
    }
    // Magnific reuses the same wrap across opens: clear our inline state whenever it (re)opens
    new MutationObserver(function () {
      if (wrap.classList.contains('mfp-ready') && !wrap.classList.contains('mfp-removing') &&
          !dragging && !wrap.classList.contains('is-settling')) {
        width = wrap.getBoundingClientRect().width || width;
        wrap.style.transform = '';
        var bg = bgEl(); if (bg) bg.style.opacity = '';
      }
      if (wrap.classList.contains('mfp-removing')) reset();
    }).observe(wrap, { attributes: true, attributeFilter: ['class'] });
    function velocity() {
      // px/s over the last ~80ms of pointer history
      var n = hist.length; if (n < 2) return 0;
      var i = n - 1, j = n - 1;
      while (j > 0 && hist[i].t - hist[j].t < 80) j--;
      var dt = (hist[i].t - hist[j].t) / 1000;
      return dt > 0 ? (hist[i].x - hist[j].x) / dt : 0;
    }
    function finish(v) {
      wrap.classList.remove('is-dragging');
      var x = currentTranslateX(wrap);
      var projected = x + project(v);
      var shouldClose = projected > width * 0.5 || v > 900;   // land where the throw is going
      wrap.classList.add('is-settling');
      if (shouldClose) {
        // Hand the finger's velocity straight into the spring so there's no seam
        spring.animateTo(width, x, Math.max(v, 400), function () {
          if (window.jQuery && jQuery.magnificPopup) jQuery.magnificPopup.close();
          reset();
        });
      } else {
        spring.animateTo(0, x, v, function () { reset(); });
      }
    }

    wrap.addEventListener('pointerdown', function (e) {
      if (reduceMotion.matches) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('input, textarea, select, button, a, [contenteditable]')) return;
      ptr = e.pointerId; startX = e.clientX; startY = e.clientY;
      decided = false; dragging = false; hist = [{ x: 0, t: e.timeStamp }];
      // Interrupt: grab from wherever it is on screen right now
      spring.stop();
      grabX = wrap.classList.contains('is-settling') ? currentTranslateX(wrap) : 0;
      wrap.classList.remove('is-settling');
    });

    wrap.addEventListener('pointermove', function (e) {
      if (ptr !== e.pointerId) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      if (!decided) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;       // hysteresis
        decided = true;
        if (Math.abs(dy) > Math.abs(dx)) { ptr = null; return; }   // vertical intent → let it scroll
        dragging = true;
        try { wrap.setPointerCapture(ptr); } catch (_) {}
        wrap.classList.add('is-dragging');
      }
      if (!dragging) return;
      var x = grabX + dx;
      if (x < 0) x = rubberband(x, width);                          // resist past the open edge
      hist.push({ x: x, t: e.timeStamp }); if (hist.length > 12) hist.shift();
      setX(x);
    });

    function end(e) {
      if (ptr !== e.pointerId) return;
      var wasDragging = dragging;
      ptr = null; dragging = false;
      if (!wasDragging) return;
      try { wrap.releasePointerCapture(e.pointerId); } catch (_) {}
      finish(velocity());
    }
    wrap.addEventListener('pointerup', end);
    wrap.addEventListener('pointercancel', end);
  }

  // Magnific Popup creates .mfp-wrap on open; watch for it
  var mo = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType === 1 && n.classList && n.classList.contains('mfp-wrap') && n.classList.contains('mfp-draw--right')) {
          attachDrawer(n);
        }
      }
    }
  });
  mo.observe(document.body, { childList: true });
})();
